import { useEffect, useState, type RefObject } from 'react';

/**
 * Whether clamped copy actually has more to show, measured rather than
 * assumed — so a Read more is only offered when it reveals something.
 *
 * Measured three times, not once. The first pass runs against whatever font
 * is available at that moment, which on a cold load is the fallback: Neue
 * Haas arrives from the Adobe kit after first paint and sets wider, so copy
 * that "fits" in three fallback lines can overflow once it swaps. The
 * observer catches a rotate or a resize doing the same thing.
 *
 * @param ref The clamped element itself, not its wrapper.
 * @param text Re-measure when the copy changes.
 */
export const useClampOverflow = (
    ref: RefObject<HTMLElement | null>,
    text: string,
): boolean => {
    const [overflows, setOverflows] = useState(false);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;
        const measure = () =>
            setOverflows(element.scrollHeight > element.clientHeight + 1);
        measure();
        document.fonts?.ready.then(measure).catch(() => {});
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
    }, [ref, text]);

    return overflows;
};
